/**
 * AthleteSeedPicker — "tap the athlete you want analyzed".
 *
 * Shown right before a clip is uploaded.  The coach scrubs to a clear frame and
 * taps the performing athlete; we send a normalised seed
 *   { frame_idx, point: [x, y] }   (x,y in [0,1], origin top-left)
 * with the upload.  The backend forwards it to the analyzer's --seed-json so the
 * tracker locks onto the chosen gymnast instead of guessing — the fix for
 * coaches/spotters in frame and teammates in matching leotards.
 *
 * Skipping is always available: with no seed the analyzer auto-designates as
 * before, so the coach is never blocked.
 *
 * Self-contained (React + the shared Button); no new CSS file.
 */
import React, { useRef, useState, useEffect, useCallback } from 'react';
import Button from '../../ui/Button.js';

const MAX_W = 640;   // canvas display cap

export default function AthleteSeedPicker({ file, fps = 30, onConfirm, onSkip }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [url] = useState(() => URL.createObjectURL(file));
  const [point, setPoint] = useState(null);   // normalised {x, y}
  const [duration, setDuration] = useState(0);
  const [time, setTime] = useState(0);
  const [dims, setDims] = useState({ w: MAX_W, h: Math.round(MAX_W * 9 / 16) });

  useEffect(() => () => { try { URL.revokeObjectURL(url); } catch (_) {} }, [url]);

  // Draw ONLY the crosshair on a transparent canvas overlaid on the visible
  // video. We deliberately do NOT drawImage(video) — iOS Safari won't decode a
  // hidden/offscreen video, so that path yields a black frame on iPad. Showing
  // the real <video> underneath and marking on top works on every browser.
  const drawFrame = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
    if (point) {
      const px = point.x * c.width, py = point.y * c.height;
      ctx.strokeStyle = '#22d3ee'; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.arc(px, py, 16, 0, Math.PI * 2); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(px - 22, py); ctx.lineTo(px + 22, py);
      ctx.moveTo(px, py - 22); ctx.lineTo(px, py + 22); ctx.stroke();
    }
  }, [point]);

  const onLoaded = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    const vw = v.videoWidth || 16, vh = v.videoHeight || 9;
    const ar = vw / vh;
    // Fit the media box inside BOTH the available width and ~half the viewport
    // height, preserving aspect. Critical on phones: a portrait clip is very
    // tall, so without a height cap it pushes the scrubber + buttons off-screen.
    const availW = Math.min(MAX_W, (window.innerWidth || MAX_W) - 48);
    const availH = Math.max(180, Math.round((window.innerHeight || 640) * 0.5));
    let w = availW, h = Math.round(w / ar);
    if (h > availH) { h = availH; w = Math.round(h * ar); }
    setDims({ w, h });
    setDuration(v.duration || 0);
    // seek a touch past the start so the first frame is decoded
    try { v.currentTime = Math.min(0.1, (v.duration || 1) / 2); } catch (_) {}
  }, []);

  useEffect(() => { drawFrame(); }, [dims, drawFrame]);

  const handleSeek = useCallback((e) => {
    const v = videoRef.current; if (!v) return;
    const t = Number(e.target.value);
    setTime(t);
    try { v.currentTime = t; } catch (_) {}
  }, []);

  const handleTap = useCallback((e) => {
    const c = canvasRef.current; if (!c) return;
    const rect = c.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    setPoint({ x: +x.toFixed(4), y: +y.toFixed(4) });
  }, []);

  useEffect(() => { drawFrame(); }, [point, drawFrame]);

  const confirm = () => {
    if (!point) return;
    const v = videoRef.current;
    const frame_idx = Math.max(0, Math.round((v?.currentTime || 0) * fps));
    onConfirm({ frame_idx, point: [point.x, point.y] });
  };

  return (
    <div style={S.backdrop} role="dialog" aria-modal="true">
      <div style={S.modal}>
        <h3 style={S.title}>Tap the athlete to analyze</h3>
        <p style={S.sub}>
          Tap the performing gymnast so we track the right person (ignore coaches,
          spotters, and teammates). Scrub to a clear frame first if needed.
        </p>

        {/* Visible video (iOS-decoded) with a transparent tap/crosshair canvas
            stacked exactly on top. Container matches the video aspect ratio so
            the two align 1:1 and normalised tap coords stay correct. */}
        <div style={{ position: 'relative', width: dims.w, height: dims.h, maxWidth: '100%', margin: '0 auto' }}>
          <video
            ref={videoRef}
            src={url}
            muted
            playsInline
            preload="auto"
            onLoadedData={onLoaded}
            onSeeked={drawFrame}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', borderRadius: 8, background: '#000' }}
          />
          <canvas
            ref={canvasRef}
            width={dims.w}
            height={dims.h}
            onClick={handleTap}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', borderRadius: 8, cursor: 'crosshair', touchAction: 'none' }}
          />
        </div>

        {duration > 0 && (
          <input
            type="range" min={0} max={duration} step={Math.max(0.01, duration / 200)}
            value={time} onChange={handleSeek} style={S.range}
            aria-label="Scrub to a clear frame"
          />
        )}

        <div style={S.row}>
          <Button variant="ghost" onClick={onSkip}>Skip — auto-detect</Button>
          <Button variant="primary" onClick={confirm} disabled={!point}>
            {point ? 'Use this athlete' : 'Tap the athlete'}
          </Button>
        </div>
      </div>
    </div>
  );
}

const S = {
  backdrop: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    padding: 16,
  },
  modal: {
    background: '#111827', color: '#f9fafb', borderRadius: 12, padding: 16,
    maxWidth: MAX_W + 40, width: '100%', boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
    // Never taller than the screen; scroll inside if content still overflows so
    // the Skip / Use-this-athlete buttons are always reachable on a phone.
    maxHeight: '92vh', overflowY: 'auto', boxSizing: 'border-box',
  },
  title: { margin: '0 0 4px', fontSize: 18, fontWeight: 700 },
  sub: { margin: '0 0 12px', fontSize: 13, color: '#9ca3af', lineHeight: 1.4 },
  canvas: {
    display: 'block', maxWidth: '100%', borderRadius: 8, cursor: 'crosshair',
    background: '#000', touchAction: 'none',
  },
  range: { width: '100%', marginTop: 12 },
  row: { display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 16 },
};
